import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { trackEvent } from '@zep/logger/tracking';
import { zusToast } from '@zep/toast';
import { openOverlayOnce, prepareOverlayOnce } from '@/shared/utils/openOverlayOnce';
import { useMyFoldersWithItems } from '../api';
import type { SaveQuizTarget, SaveToFolderOptions } from '../types/saveQuizLocation.types';
import { reportFolderError, toFolderSaveContext, toTrackingFailReason } from '../utils/folderTracking';
import { QUIZ_SAVE_TOAST_POSITION } from '../utils/quizSaveFeedback';
const OVERLAY_ID = 'save-to-folder-modal';

/**
 * 방금 받아 온 목록을 "새 것"으로 인정하는 시간. 연속으로 여닫을 때 매번 기다리지 않게 한다.
 * 정확성은 무효화가 지킨다.
 */
const FOLDER_LIST_FRESH_MS = 60_000;

/**
 * 저장 위치 선택 모달을 목록을 받아 둔 뒤 연다. 모달 안에서 받으면 폴더가 도착하면서
 * 높이가 점프하고, "폴더가 없는 사용자"와 "아직 안 받은 상태"가 구분되지 않는다.
 */
export const useShowSaveToFolderModal = () => {
  const {
    t
  } = useTranslation(['common']);
  const queryClient = useQueryClient();
  return useCallback((quiz: SaveQuizTarget, options?: SaveToFolderOptions) => prepareOverlayOnce(OVERLAY_ID, async () => {
    const context = toFolderSaveContext(quiz);
    // 여기까지 왔으면 로그인한 사용자다. 비로그인은 호출부가 로그인으로 보내며 따로 찍는다.
    trackEvent('quiz_folder_save_entry_click', {
      ...context,
      auth_required: false
    });

    // 목록과 모달 코드를 동시에 받는다. 직렬로 받으면 느린 망에서 기다림이 두 배가 된다.
    const prepared = await Promise.all([queryClient.fetchInfiniteQuery({
      ...useMyFoldersWithItems.getFetchOptions(),
      staleTime: FOLDER_LIST_FRESH_MS,
      // 전역 기본 재시도는 간격이 30초까지 늘어난다.
      // 그동안 버튼이 죽은 것처럼 보이므로 첫 실패에 바로 알린다.
      retry: false
    }), import('../components/SaveToFolderModal')]).catch((error: unknown) => {
      trackEvent('quiz_folder_save_modal_fail', {
        ...context,
        reason: toTrackingFailReason(error)
      });
      reportFolderError(error, {
        action: 'save_modal_open',
        quizSetId: quiz.id
      });
      zusToast.error(t('common:folderSave.modal.loadError'), {
        position: QUIZ_SAVE_TOAST_POSITION
      });
      return null;
    });
    if (!prepared) return;
    const [list, {
      SaveToFolderModal
    }] = prepared;
    trackEvent('quiz_folder_save_modal_open', {
      ...context,
      from_folder_id: quiz.currentFolderId,
      folder_count: list.pages.flatMap(page => page.items).length,
      private_only: !!options?.privateOnly
    });
    openOverlayOnce(OVERLAY_ID, ({
      unmount
    }) => <SaveToFolderModal quiz={quiz} options={options} onClose={unmount} />);
  }), [queryClient, t]);
};